import { supabase } from '../lib/supabase';

/**
 * Interface para o perfil do usuário
 */
export interface UserProfile {
    id: string; // PK e FK para auth.users.id
    primeiro_acesso: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Busca o perfil do usuário autenticado
 */
export async function fetchUserProfile(): Promise<UserProfile | null> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
        throw new Error('Usuário não autenticado.');
    }

    // A tabela profiles usa 'id' como PK/FK, não 'user_id'
    // Tenta buscar usando maybeSingle() ao invés de single() para evitar erro quando não existe
    const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id) // ✅ CORRIGIDO: usa 'id' ao invés de 'user_id'
        .maybeSingle(); // maybeSingle retorna null se não encontrar, ao invés de erro

    if (error) {
        // PGRST116 = não encontrado
        if (error.code === 'PGRST116') {
            return null;
        }

        // Se for erro de permissão (42501), retorna null como fallback seguro
        if (error.code === '42501' || error.message?.includes('permission')) {
            return null;
        }

        // Para qualquer outro erro, retorna null também (fallback seguro)
        return null;
    }

    if (!data) {
        return null;
    }

    return data as UserProfile;
}

/**
 * Verifica se é o primeiro acesso do usuário
 * @returns true se for primeiro acesso, false caso contrário
 */
export async function isPrimeiroAcesso(): Promise<boolean> {
    try {
        const profile = await fetchUserProfile();

        if (!profile) {
            return true;
        }

        return profile.primeiro_acesso;
    } catch {
        // Em caso de erro, assume que NÃO é primeiro acesso para não forçar tour
        return false;
    }
}

/**
 * Marca que o usuário completou o primeiro acesso (tour concluído)
 */
export async function markPrimeiroAcessoCompleto(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
        throw new Error('Usuário não autenticado.');
    }

    // A tabela profiles usa 'id' como PK/FK, não 'user_id'
    const { error } = await supabase
        .from('profiles')
        .update({ primeiro_acesso: false })
        .eq('id', user.id);  // ✅ CORRIGIDO: usa 'id' ao invés de 'user_id'

    if (error) {
        throw new Error('Falha ao atualizar status de primeiro acesso.');
    }
}
